/**
 * @typedef {Object} activityLogField
 * @property {string} label Field label
 * @property {'list'
 * | 'kvMap'
 * | 'boolean'
 * | 'events'
 * | 'currency'
 * | 'user'
 * | 'status'
 * | 'json_viewer'
 * | 'countries'
 * | 'platform'
 * | 'capitalize'
 * | 'postbacks'
 * | 'management'
 * | 'management-opposite'
 * | 'timeframe-schedule'
 * | 'frequency-cap'
 * | 'impression-rate'} [type] Field type. If not specified, value will be treated as a string
 * @property {boolean} [ignore] If true, field won't be shown in the table
 */

/** @type {Record<string, activityLogField>} */
const activityLogFieldsMap = {
  status: {
    label: 'Status',
    type: 'status',
  },
  risk_management: {
    label: 'Risk Management',
  },
  payout_type: {
    label: 'Payout Type',
  },
  comments: {
    label: 'Comments',
  },
  user_id: {
    label: 'Account Manager',
    type: 'user',
  },
  name: {
    label: 'Name',
  },
  mmp: {
    label: 'MMP Type',
  },
  mmp_account_id: {
    label: 'MMP Account',
  },
  currency: {
    label: 'Currency',
  },
  timezone: {
    label: 'Offer Timezone',
  },
  ip_black_list: {
    label: 'IP Blacklist',
  },
  daily_click_cap: {
    label: 'Daily Click Cap',
  },
  clicks_frequency: {
    label: 'Clicks Volume',
  },
  daily_install_cap: {
    label: 'Daily Install Cap',
  },
  tracking_url: {
    label: 'Click URL',
  },
  payout: {
    label: 'Payout',
    type: 'currency',
  },
  appname_blacklist: {
    label: 'Appname Blacklist',
    type: 'list',
  },
  geo_targeting: {
    label: 'Geo Targeting',
    type: 'countries',
    //     whitelist:
    // - Israel
    //        - Tel Aviv
    //        - Jerusalem
  },
  geo_targeting_hash: {
    label: 'Geo Targeting Hash',
    ignore: true,
  },
  platform: {
    label: 'Platform',
    type: 'platform',
  },
  country: {
    label: 'Country',
  },
  custom_params: {
    label: 'Custom Params',
    type: 'json_viewer',
  },
  custom_params_list: {
    label: 'Custom Params List',
    type: 'list',
  },
  app_name_mode: {
    label: 'App Name Mode',
  },
  bundle_id_mode: {
    label: 'Bundle ID Mode',
  },
  subsource_mode: {
    label: 'Subsource Mode',
  },
  referer_mode: {
    label: 'Referer Mode',
  },
  deviceid_mode: {
    label: 'Device ID Mode',
  },
  distribution_format: {
    label: 'Distribution Format',
  },
  impression_pacing: {
    label: 'Impression Pacing',
  },
  impression_pacing_hour: {
    label: 'Impression Pacing Hour',
  },
  click_pacing: {
    label: 'Click Pacing',
  },
  click_pacing_hour: {
    label: 'Click Pacing Hour',
  },
  weekly_conversion_cap: {
    label: 'Weekly Conversion Cap',
  },
  daily_conversion_cap: {
    label: 'Daily Conversion Cap',
  },
  daily_impression_cap: {
    label: 'Daily Impression Cap',
  },
  monthly_budget: {
    label: 'Monthly Budget',
  },
  daily_budget: {
    label: 'Daily Budget',
  },
  deviceid_blacklist_id: {
    label: 'Device ID Blacklist ID',
  },
  block_pixels: {
    label: 'Block Pixels',
    type: 'boolean',
  },
  block_proxies: {
    label: 'Block Proxies',
    type: 'boolean',
  },
  block_iframe: {
    label: 'Block iFrame',
    type: 'boolean',
  },
  normalize_bundleid: {
    label: 'Normalize Bundle ID',
    type: 'boolean',
  },
  enforce_install_delivery: {
    label: 'Pause offer when goal is reached',
    type: 'boolean',
  },
  enforce_non_rw_share: {
    label: 'Pause rewarded placements when goal is reached',
    type: 'boolean',
  },
  impressions_to_install_goal: {
    label: 'Impressions to install goal',
  },
  hide_caps: {
    label: 'Hide Caps',
    type: 'boolean',
  },
  impression_rate: {
    label: 'Impression Rate',
    type: 'impression-rate',
  },
  frequency_cap_params: {
    label: 'Frequency Cap',
    type: 'frequency-cap',
  },
  device_type_blacklist: {
    label: 'Device Type Restrictions',
    type: 'list',
  },
  isp_whitelist: {
    label: 'ISP Whitelist',
    type: 'list',
  },
  isp_blacklist: {
    label: 'ISP Blacklist',
    type: 'list',
  },
  carrier_whitelist: {
    label: 'Carrier Whitelist',
    type: 'list',
  },
  carrier_blacklist: {
    label: 'Carrier Blacklist',
    type: 'list',
  },
  allow_no_isp: {
    label: 'Allow No ISP',
    type: 'boolean',
  },
  allow_no_carrier: {
    label: 'Allow No Carrier',
    type: 'boolean',
  },
  should_block_wifi: {
    label: 'Block WiFi',
    type: 'boolean',
  },
  os_version_blacklist: {
    label: 'OS Version Blacklist',
    type: 'list',
  },
  advanced_targeting_whitelist: {
    label: 'Advanced Targeting Whitelist',
    type: 'json_viewer',
  },
  advanced_targeting_blacklist: {
    label: 'Advanced Targeting Blacklist',
    type: 'json_viewer',
  },
  require_validation: {
    label: 'Require Validation',
    type: 'boolean',
  },
  allow_incent: {
    label: 'Allow Incent',
    type: 'boolean',
  },
  hard_kpi: {
    label: 'Hard KPI',
    type: 'boolean',
  },
  store_package_id: {
    label: 'Store Package ID',
  },
  store_bundle_id: {
    label: 'Store Bundle ID',
  },
  preview_url: {
    label: 'Preview URL',
  },
  is_direct_link: {
    label: 'Is Direct Link',
    type: 'boolean',
  },
  campaign_type: {
    label: 'Campaign Type',
  },
  fraud_tool: {
    label: 'Fraud Tool',
  },
  impression_url: {
    label: 'Impression URL',
  },
  kpi: {
    label: 'KPI',
  },
  user_flow: {
    label: 'User Flow',
  },
  short_description: {
    label: 'Short Description',
  },
  description: {
    label: 'Description',
  },
  restriction_hash: {
    label: 'Resriction Hash',
    ignore: true,
  },
  restriction_list: {
    label: 'Restriction List',
    type: 'list',
  },
  date_end: {
    label: 'Date End',
  },
  date_start: {
    label: 'Date Start',
  },
  event_convert_payout_only: {
    label: 'Event Convert Payout Only',
    type: 'boolean',
  },
  report_retention: {
    label: 'Report Retention',
    type: 'boolean',
  },
  events: {
    label: 'Events',
    type: 'events',
    // - jump (Jump) - 1$ fixed
  },
  debug: {
    label: 'Debug',
    type: 'boolean',
  },
  allow_multiple_conversions: {
    label: 'Allow Multiple Conversions',
    type: 'boolean',
  },
  sub1_partial_match_count: {
    label: 'Sub1 Partial Match Count',
  },
  restrict_timeframe: {
    label: 'Restrict Timeframe',
    type: 'boolean',
  },
  sub1_whitelist: {
    label: 'Sub1 Whitelist',
    ignore: true,
  },
  sub1_blwl_mode: {
    label: 'Sub1 BLWL Mode',
    type: 'capitalize',
  },
  default_subsource_cap: {
    label: 'Default Subsource Cap',
  },
  redirect_type: {
    label: 'Redirect Type',
  },
  custom_report: {
    label: 'Custom Report',
    type: 'boolean',
  },
  report_on_overcap: {
    label: 'Report On Overcap',
    type: 'boolean',
  },
  report_offer_closed: {
    label: 'Report Offer Closed',
    type: 'boolean',
  },
  payout_mode: {
    label: 'Payout Mode',
    type: 'capitalize',
  },
  override_name: {
    label: 'Override Name',
  },
  sub1_whitelist_hash: {
    label: 'Sub1 Whitelist Hash',
    ignore: true,
  },
  sub1_whitelist_list: {
    label: 'Sub1 Whitelist List',
    type: 'list',
  },
  restrict_timeframe_schedule: {
    label: 'Restrict Timeframe Schedule',
    type: 'timeframe-schedule',
  },
  default_event_convert_payout: {
    label: 'Default Event Convert Payout',
    type: 'boolean',
  },
  default_event_managed: {
    label: 'Default Event Managed',
    type: 'boolean',
  },
  default_offer_cap: {
    label: 'Default Offer Cap',
  },
  auto_placements: {
    label: 'Auto Placements',
    type: 'boolean',
  },
  scan_frequency: {
    label: 'Scan Frequency',
  },
  api_load_active_only: {
    label: 'API Load Active Only',
    type: 'boolean',
  },
  mask_source: {
    label: 'Mask Source',
    type: 'boolean',
  },
  require_secret: {
    label: 'Require Secret',
    type: 'boolean',
  },
  total_subsource_limit: {
    label: 'Total Subsource Limit',
  },
  daily_subsource_limit: {
    label: 'Daily Subsource Limit',
  },
  ip_whitelist: {
    label: 'IP Whitelist',
  },
  custom_install_event_name: {
    label: 'Custom Install Event Name',
  },
  fetch_api_override_events: {
    label: 'Fetch API Override Events',
    type: 'boolean',
  },
  fetch_api_override_tracking: {
    label: 'Fetch API Override Tracking',
    type: 'boolean',
  },
  api_lock_defaults: {
    label: 'API Offer Updates',
    type: 'json_viewer',
  },
  default_require_validation: {
    label: 'Default Require Validation',
    type: 'boolean',
  },
  panel_password: {
    label: 'Panel Password',
  },
  panel_username: {
    label: 'Panel Username',
  },
  panel_url: {
    label: 'Panel URL',
  },
  group_name: {
    label: 'Group Name',
  },
  pb_params: {
    label: 'Postback Params',
    type: 'json_viewer',
  },
  tracking_link_params: {
    label: 'Tracking Link Params',
    type: 'json_viewer',
  },
  integration_params: {
    label: 'Integration Params',
    type: 'json_viewer',
  },
  conversion_sync_enabled: {
    label: 'Conversion Sync Enabled',
    type: 'boolean',
  },
  conversion_sync_type: {
    label: 'Conversion Sync Type',
  },
  conversion_sync_params: {
    label: 'Conversion Sync Params',
    type: 'json_viewer',
  },
  conversion_sync_state: {
    label: 'Conversion Sync State',
    ignore: true,
  },
  last_conversion_sync_at: {
    label: 'Last Conversion Sync',
  },
  sub2_blacklist: {
    label: 'Sub2 Blacklist',
  },
  sub2_blwl_mode: {
    label: 'Sub2 BLWL Mode',
    type: 'capitalize',
  },
  sub1_blacklist: {
    label: 'Sub1 Blacklist',
    ignore: true,
  },
  /** @deprecated - Old field name, changed to payable_events */
  payable_event_name: {
    label: 'Payable Event Name',
  },
  payable_events: {
    label: 'Payable Events',
    type: 'list',
  },
  sub2_blacklist_hash: {
    label: 'Sub2 Blacklist Hash',
    ignore: true,
  },
  sub2_blacklist_list: {
    label: 'Sub2 Blacklist List',
    type: 'list',
  },
  sub1_blacklist_hash: {
    label: 'Sub1 Blacklist Hash',
    ignore: true,
  },
  sub1_blacklist_list: {
    label: 'Sub1 Blacklist List',
    type: 'list',
  },
  ignore_freq_cap: {
    label: 'Ignore Freq Cap',
    type: 'boolean',
  },
  ignore_blocks: {
    label: 'Ignore Blocks',
    type: 'json_viewer',
  },
  media_type: {
    label: 'Media Type',
    type: 'capitalize',
  },
  show_payable_only: {
    label: 'Show Payable Only',
    type: 'boolean',
  },
  report_payable_installs_only: {
    label: 'Report Payable Installs Only',
    type: 'boolean',
  },
  report_payable_events_only: {
    label: 'Report Payable Events Only',
    type: 'boolean',
  },
  default_placement_payout: {
    label: 'Default Placement Payout',
  },
  postbacks: {
    label: 'Postbacks',
    type: 'postbacks',
  },
  sub2_whitelist_hash: {
    label: 'Sub2 Whitelist Hash',
    ignore: true,
  },
  sub2_whitelist_list: {
    label: 'Sub2 Whitelist List',
    type: 'list',
  },
  restrict_timeframe_timezone: {
    label: 'Timeframe Timezone',
  },
  placement_defaults: {
    label: 'Placement Defaults',
    type: 'json_viewer',
  },
  connected_offers_weights: {
    label: 'Connected Offer Weights',
    type: 'kvMap',
  },
  lock_geo_targeting: {
    label: 'Geo Targeting Management',
    type: 'management',
  },
  lock_events: {
    label: 'Events Management',
    type: 'management',
  },
  lock_tracking_url: {
    label: 'Tracking URL Management',
    type: 'management',
  },
  lock_conversion_caps: {
    label: 'Conversion Caps Management',
    type: 'management',
  },
  lock_caps: {
    label: 'Caps Management',
    type: 'management',
  },
  lock_sub1: {
    label: 'Pub.Subsource Management',
    type: 'management',
  },
  lock_fields: {
    label: 'API Lock Fields',
    type: 'json_viewer',
  },
  auto_payout: {
    label: 'Payout Management',
    type: 'management-opposite',
  },
  os_targeting: {
    label: 'OS Targeting',
  },
  os_targeting_version_min: {
    label: 'OS Targeting Min Version',
  },
  os_targeting_version_max: {
    label: 'OS Targeting Max Version',
  },
  subsource_last_override: {
    label: 'Subsource Last Override',
    ignore: true,
  },
  subsource_auto_total: {
    label: 'Number of generated subsources',
  },
  subsource_auto_frequency: {
    label: 'Frequency of generated subsources',
  },
  override_sources: {
    label: 'Override Sources',
    type: 'list',
  },
  integration_type: {
    label: 'Integration Type',
    type: 'capitalize',
  },
  override_tracking_params: {
    label: 'Override Tracking Params',
  },
  advanced_privacy_support: {
    label: 'Advanced Privacy Support',
    type: 'boolean',
  },
  convert_closed: {
    label: 'Keep converting even if offer is cancelled',
    type: 'boolean',
  },
  offer_template_id: {
    label: 'Offer Template',
  },
  geo_targeting_mode: {
    label: 'Geo Targeting Mode',
  },
  subsource_auto_generate: {
    label: 'Auto-generate Subsources',
    type: 'boolean',
  },
  subsource_auto_click_cap_generate: {
    label: 'Generate Subsources by Click Cap',
    type: 'boolean',
  },
  subsource_auto_optimize: {
    label: 'Replace Generated Sub on Rejection',
    type: 'boolean',
  },
  cr_optimization_method: {
    label: 'CR Optimization Method',
  },
  should_auto_update_app_version: {
    label: 'Auto-update App Version',
    type: 'boolean',
  },
  should_auto_update_sdk_version: {
    label: 'Auto-update SDK Version',
    type: 'boolean',
  },
  should_disable_conversion: {
    label: 'Disable Conversions',
    type: 'boolean',
  },
  should_ignore_incomplete_user_flow: {
    label: 'Ignore Incomplete User Flow',
    type: 'boolean',
  },
  rules: {
    label: 'KPI Rules',
    type: 'json_viewer',
  },
  are_events_unique: {
    label: 'Unique Events',
    type: 'boolean',
  },
};

export const shouldShowField = (field) => {
  if (!activityLogFieldsMap[field]) return true;
  return !activityLogFieldsMap[field].ignore;
};

export const getFieldType = (field) => {
  if (!activityLogFieldsMap[field]) return;
  return activityLogFieldsMap[field].type;
};

export const getFieldLabel = (field, opts) => {
  if (field === 'impression_rate' && opts && opts.impressionRateLabel) {
    return opts.impressionRateLabel;
  }
  if (!activityLogFieldsMap[field]) return field;
  return activityLogFieldsMap[field].label;
};
